import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Bot, Hash, Mail, MessageSquare, Phone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Switch } from '@/components/atoms/switch';
import { Skeleton } from '@/components/atoms/skeleton';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import { usePhoneNumbers } from '@/lib/messaging/hooks';
import {
  useCreateChannel,
  useDeleteChannel,
  useInboxes,
  useManagedInboxes,
  useUpdateChannel,
} from '@/lib/messaging/useInboxes';
import { useHasPermission } from '@/hooks/usePermissions';
import { useUsers } from '@/hooks/settings/useUsers';
import { useEmailAccounts } from '@/hooks/email/useEmailAccounts';
import { useAiReceptionists, type AiReceptionist } from '@/lib/ai/receptionists';
import { ChannelAccessControl } from '@/components/messaging/ChannelAccessControl';
import { WebChatWidgetsSection } from '@/components/messaging/WebChatWidgetsSection';
import type { Channel, CreateChannelPayload, ManagedChannel } from '@/lib/messaging/types';
import { Link } from '@tanstack/react-router';

export const Route = createFileRoute('/_dashboard/dashboard/settings/channels')({
  component: ChannelsSettingsPage,
});

/** The mailbox block the management list carries (absent on the visible list). */
function managedMailboxOf(c: Channel): ManagedChannel['mailbox'] | undefined {
  return (c as Partial<ManagedChannel>).mailbox ?? undefined;
}

function ChannelsSettingsPage() {
  // Managers get every channel, including personal mailboxes they cannot read
  // (so they can share or disconnect them); everyone else gets what they see.
  const canManageChannels = useHasPermission('channels:manage');
  const visible = useInboxes();
  const managed = useManagedInboxes(canManageChannels);
  const { data: channels = [], isLoading } = canManageChannels ? managed : visible;
  const { data: usersResp } = useUsers();
  const userNameById = new Map(
    (usersResp?.data ?? []).map((u) => [
      u.id,
      u.displayName?.trim() ||
        [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
        u.email,
    ]),
  );
  const { data: phoneData } = usePhoneNumbers();
  const { data: emailData } = useEmailAccounts();
  const { data: receptionists = [] } = useAiReceptionists();

  const numbers = phoneData?.data ?? [];
  const mailboxes = emailData ?? [];

  // The list endpoint returns only the bound resource *id*, so join to the
  // tenant's numbers/mailboxes here to show the actual phone number / email
  // address on each channel row.
  const phoneById = new Map(numbers.map((n) => [n.id, n]));
  const mailboxById = new Map(mailboxes.map((m) => [m.id, m]));
  const resourceOf = (c: Channel): string | null => {
    if (c.kind === 'PHONE') {
      const e164 = c.phoneNumberId ? phoneById.get(c.phoneNumberId)?.e164 : null;
      return e164 ? formatPhoneDisplay(e164) : null;
    }
    if (c.kind === 'WEB_CHAT') return 'Embeddable widget';
    return (
      (c.channelAccountId ? mailboxById.get(c.channelAccountId)?.emailAddress : null) ??
      managedMailboxOf(c)?.emailAddress ??
      null
    );
  };

  // Who owns a mailbox channel and whether this viewer may change its sharing.
  // The management list says so directly; on the visible list the viewer can
  // manage exactly the mailboxes they own.
  const accessOf = (c: Channel): { ownerName: string | null; canManageAccess: boolean } => {
    if (c.kind !== 'EMAIL') return { ownerName: null, canManageAccess: true };
    const managedMailbox = managedMailboxOf(c);
    const ownerUserId = managedMailbox?.ownerUserId ?? null;
    return {
      ownerName: ownerUserId ? (userNameById.get(ownerUserId) ?? null) : null,
      canManageAccess:
        managedMailbox?.canManageAccess ??
        (c.channelAccountId ? mailboxById.get(c.channelAccountId)?.isOwner === true : false),
    };
  };

  // A resource is "available" when no channel already binds it.
  const boundPhone = new Set(channels.map((c) => c.phoneNumberId).filter(Boolean) as string[]);
  const boundMailbox = new Set(channels.map((c) => c.channelAccountId).filter(Boolean) as string[]);
  const availableNumbers = numbers.filter((n) => !boundPhone.has(n.id));
  const availableMailboxes = mailboxes.filter((m) => !boundMailbox.has(m.id));

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">Channels</h1>
            <p className="text-xs text-muted-foreground">
              Name your inboxes and point each at a phone number or mailbox.
            </p>
          </div>
        }
        contentClassName="px-4 py-4 md:px-5 md:py-5"
      >
        {isLoading ? (
          <div className="max-w-2xl space-y-4">
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        ) : (
          <div className="max-w-2xl space-y-5">
            <NewChannelCard
              availableNumbers={availableNumbers.map((n) => ({
                id: n.id,
                label: n.label
                  ? `${n.label} · ${formatPhoneDisplay(n.e164)}`
                  : formatPhoneDisplay(n.e164),
              }))}
              availableMailboxes={availableMailboxes.map((m) => ({
                id: m.id,
                label: m.displayName ? `${m.displayName} · ${m.emailAddress}` : m.emailAddress,
              }))}
            />

            <div className="overflow-hidden rounded-xl border">
              <div className="border-b bg-muted/30 px-4 py-3">
                <p className="text-sm font-medium">Your channels</p>
                <p className="text-xs text-muted-foreground">
                  {channels.length} channel{channels.length === 1 ? '' : 's'} ·{' '}
                  <Link
                    to="/dashboard/settings/chatbot"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    Manage AI receptionists
                  </Link>
                </p>
              </div>
              {channels.length === 0 ? (
                <div className="flex items-center gap-3 px-4 py-6 text-sm text-muted-foreground">
                  <Hash className="h-4 w-4 shrink-0" />
                  No channels yet — create one above.
                </div>
              ) : (
                <ul className="divide-y">
                  {channels.map((c) => (
                    <ChannelRow
                      key={c.id}
                      channel={c}
                      resource={resourceOf(c)}
                      receptionists={receptionists}
                      {...accessOf(c)}
                    />
                  ))}
                </ul>
              )}
            </div>

            <WebChatWidgetsSection />
          </div>
        )}
      </DetailPageLayout>
    </>
  );
}

// ─── New channel ───────────────────────────────────────────────────────────────

function NewChannelCard({
  availableNumbers,
  availableMailboxes,
}: {
  availableNumbers: { id: string; label: string }[];
  availableMailboxes: { id: string; label: string }[];
}) {
  const create = useCreateChannel();
  const [name, setName] = useState('');
  // Resource select value is encoded "phone:<id>" | "email:<id>".
  const [resource, setResource] = useState('');

  const hasResources = availableNumbers.length > 0 || availableMailboxes.length > 0;

  const submit = () => {
    if (!name.trim() || !resource) return;
    const [kind, id] = resource.split(':');
    const payload: CreateChannelPayload =
      kind === 'phone'
        ? { name: name.trim(), kind: 'PHONE', phoneNumberId: id }
        : { name: name.trim(), kind: 'EMAIL', channelAccountId: id };
    create.mutate(payload, {
      onSuccess: () => {
        toast.success(`Channel “${name.trim()}” created`);
        setName('');
        setResource('');
      },
      onError: (err) =>
        toast.error((err as { message?: string })?.message ?? 'Could not create channel'),
    });
  };

  return (
    <div className="rounded-xl border p-4">
      <p className="text-sm font-medium">New channel</p>
      {hasResources ? (
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="channel-name">Name</Label>
            <Input
              id="channel-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sales"
              maxLength={80}
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="channel-resource">Number or mailbox</Label>
            <select
              id="channel-resource"
              value={resource}
              onChange={(e) => setResource(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Select…</option>
              {availableNumbers.length > 0 && (
                <optgroup label="Phone numbers">
                  {availableNumbers.map((n) => (
                    <option key={n.id} value={`phone:${n.id}`}>
                      {n.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {availableMailboxes.length > 0 && (
                <optgroup label="Mailboxes">
                  {availableMailboxes.map((m) => (
                    <option key={m.id} value={`email:${m.id}`}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <Button onClick={submit} disabled={!name.trim() || !resource || create.isPending}>
            Create
          </Button>
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          Every number and mailbox already backs a channel. Provision a number or connect a mailbox
          to add another.
        </p>
      )}
    </div>
  );
}

// ─── Channel row ────────────────────────────────────────────────────────────────

function ChannelRow({
  channel,
  resource,
  receptionists,
  ownerName,
  canManageAccess,
}: {
  channel: Channel;
  resource: string | null;
  receptionists: AiReceptionist[];
  ownerName: string | null;
  canManageAccess: boolean;
}) {
  const update = useUpdateChannel();
  const del = useDeleteChannel();
  const [name, setName] = useState(channel.name);
  const Icon =
    channel.kind === 'EMAIL' ? Mail : channel.kind === 'WEB_CHAT' ? MessageSquare : Phone;
  // Phone-backed channels carry SMS (and voice); label them "SMS" rather than
  // the internal "phone" kind so operators recognise the medium.
  const kindLabel =
    channel.kind === 'EMAIL' ? 'Email' : channel.kind === 'WEB_CHAT' ? 'Web Chat' : 'SMS';

  const commitName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === channel.name) {
      setName(channel.name);
      return;
    }
    update.mutate(
      { id: channel.id, name: trimmed },
      {
        onError: (err) => {
          setName(channel.name);
          toast.error((err as { message?: string })?.message ?? 'Could not rename channel');
        },
      },
    );
  };

  // "No AI bot" (empty value) clears the assignment; picking a bot sets it.
  const selectReceptionist = (agentRoleId: string) =>
    update.mutate(
      { id: channel.id, aiReceptionistAgentRoleId: agentRoleId || null },
      {
        onError: (err) =>
          toast.error((err as { message?: string })?.message ?? 'Could not update AI receptionist'),
      },
    );

  // Only meaningful once a bot answers this channel: it changes what the bot
  // does with a scheduling request, so it is hidden when there is no bot.
  const toggleBookingHandoff = (enabled: boolean) =>
    update.mutate(
      { id: channel.id, aiBookingHandoffEnabled: enabled },
      {
        onError: (err) =>
          toast.error(
            (err as { message?: string })?.message ?? 'Could not update booking hand-off',
          ),
      },
    );

  const remove = () => {
    if (
      !window.confirm(
        `Delete “${channel.name}”? Its conversations are removed and the number/mailbox is freed.`,
      )
    ) {
      return;
    }
    del.mutate(channel.id, {
      onSuccess: () => toast.success(`Channel “${channel.name}” deleted`),
    });
  };

  // Someone else's personal mailbox: listed so its sharing is visible, but only
  // its owner or an admin may rename it, assign Iris or delete it (the API
  // refuses anyone else), so don't offer controls that can only fail.
  const locked = !canManageAccess;
  const lockedTitle = locked
    ? 'Only the mailbox owner or an admin can change this channel'
    : undefined;

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        {/* Identity — name on top, "SMS · +1 …" resource line beneath. */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              className="h-8 w-full max-w-[260px]"
              maxLength={80}
              disabled={locked}
              title={lockedTitle}
            />
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Badge variant="outline" className="h-4 px-1 text-[10px] font-medium">
                {kindLabel}
              </Badge>
              <span className="truncate font-mono" title={resource ?? undefined}>
                {resource ?? <span className="italic opacity-70">not linked</span>}
              </span>
            </p>
          </div>
        </div>

        {/* AI receptionist selector + delete. */}
        <div className="flex shrink-0 items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Bot className="h-3.5 w-3.5 shrink-0" />
            <span className="hidden sm:inline">AI receptionist</span>
            <select
              value={channel.aiReceptionistAgentRoleId ?? ''}
              onChange={(e) => selectReceptionist(e.target.value)}
              disabled={update.isPending || locked}
              title={lockedTitle}
              className="h-8 min-w-[9rem] rounded-md border border-input bg-background px-2 text-xs text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
              aria-label={`AI receptionist for ${channel.name}`}
            >
              <option value="">No AI bot</option>
              {receptionists
                // A channel greets customers through the entry-point bot only —
                // specialists (Scheduler/Estimator) are reached via routing, not
                // bound directly. Keep the current value listed if a legacy
                // channel still points at a non-entrypoint bot.
                .filter((r) => r.isEntrypoint || r.id === channel.aiReceptionistAgentRoleId)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.displayName}
                    {r.enabled ? '' : ' (disabled)'}
                  </option>
                ))}
            </select>
          </label>
          {/* Only web chat can render the booking CTA today — an SMS/email body
              ships verbatim, so offering the toggle there would let an operator
              enable a path the customer never sees. */}
          {channel.aiReceptionistAgentRoleId && channel.kind === 'WEB_CHAT' && (
            <label
              className="flex items-center gap-2 text-xs text-muted-foreground"
              title="Instead of booking in the conversation, the bot invites the customer to open the booking widget — which prices the job from your pricebook and shows tax for their address. Requires the booking widget embedded on the same page."
            >
              <Switch
                checked={channel.aiBookingHandoffEnabled === true}
                onCheckedChange={toggleBookingHandoff}
                disabled={update.isPending}
                aria-label={`Hand scheduling to the booking widget for ${channel.name}`}
              />
              <span className="hidden lg:inline">Book via widget</span>
            </label>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={remove}
            disabled={del.isPending || locked}
            title={lockedTitle}
            aria-label={`Delete ${channel.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Per-channel access control — restrict who can see & reply. Web-chat
          channels are tenant-shared (no per-widget ACL yet), so we don't show
          the access controls for them — they'd imply a restriction the backend
          does not enforce. */}
      {channel.kind !== 'WEB_CHAT' && (
        <ChannelAccessControl channel={channel} ownerName={ownerName} canManage={canManageAccess} />
      )}
    </li>
  );
}
